import { Injectable, ErrorHandler, NgZone } from "@angular/core";
import { HttpErrorResponse } from "@angular/common/http";
import { Router } from "@angular/router";
import { EmptyError } from "rxjs";
import { MonitoringService } from "./monitoring.service";
import { isAuthUiError, isGraphApiUiError } from "./ui-error.factory";
import { UiErrorService } from "./ui-error-service/ui-error.service";

@Injectable({
  providedIn: "root"
})
export class ErrorService implements ErrorHandler {
  /**
   * used in error-general for UI purposes!
   */
  public static currentCapturedError: HttpErrorResponse;
  public static currentOperationId: string;

  constructor(
    private readonly router: Router,
    private readonly ngZone: NgZone,
    private readonly monitoringService: MonitoringService,
    private readonly uiErrorService: UiErrorService
  ) { }

  handleError(error: any): void {
    if (error.rejection) {
      error = error.rejection;
    }

    if (error instanceof EmptyError) {
      // EmptyError is thrown by firstValueFrom/lastValueFrom when the source observable
      // completes without emitting. In practice this only happens when an in-flight HTTP
      // request is cancelled by CancelHttpBetweenRoutesInterceptor on navigation.
      // The Office.js observables (outlookService.to/cc/bcc) wrap Office callbacks via
      // observableOfOutlookAsync, which always emits before completing and can never produce
      // EmptyError. All HTTP calls go through the interceptor, so this guard is scoped
      // correctly and not a catch-all.
      return;
    }

    if (isChunkLoadingError(error)) {
      // try force cache-invalidation if loading a chunk fails.
      // probably a new version deployed!
      this.reloadPage();
      return;
    }

    // error-specific stuff
    ErrorService.currentCapturedError = error;
    ErrorService.currentOperationId = this.resolveOperationId(error);
    try {
      this.monitoringService.logError(error);
    } catch { }

    // In the future, we aim to consolidate all error handling into a single service that displays errors in the same banner.
    // This will replace the current approach of redirecting to the error page, shifting to banner-based error notifications instead.
    if (isAuthUiError(error)) {
      this.uiErrorService.show(error);
      console.error("SOFO: AuthUiError caught by general error handler:", error);
      return;
    }

    if (isGraphApiUiError(error)) {
      this.uiErrorService.show(error);
      console.error("SOFO: GraphApiError caught by general error handler:", error);
      return;
    }

    if (error instanceof HttpErrorResponse) {
      console.error("SOFO: Error occurred when accessing a HTTP resource. Redirecting to error-page!", error);
    } else {
      console.error("SOFO: Unexpected error. Redirecting to error-page!", error);
    }

    this.redirect("/error/general");
  }

  private resolveOperationId(error: any): string {
    if (error instanceof HttpErrorResponse) {
      const operationId = error.error?.traceId;
      if (operationId) {
        return operationId;
      }
    }

    return this.monitoringService.getOperationId() || this.monitoringService.getSessionId();
  }

  protected reloadPage(): void {
    window.location.reload();
  }

  private redirect(url: string): void {
    // redirect does not work consistently without timeout. NFI why
    setTimeout(() => {
      // avoid warnings about navigation outside ng-zones.
      this.ngZone.run(() => {
        // actually redirect
        this.router.navigateByUrl(url);
      });
    }, 100);
  }
}

export function isChunkLoadingError(error: any): boolean {
  const chunkFailedMessage = /Loading chunk [^\W_]+ failed/;
  return chunkFailedMessage.test(error?.message);
}
